/* eslint-disable @typescript-eslint/no-unused-vars */
import axios, { AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import { decryptData, encryptData } from './middleware';
import { AuthUser, baseURL, decrypt, encrypt, encryptDynamicPath } from '../Components/Common/Util';
import { store } from 'slices/persister';
// import { closeWebSocket } from '../Components/Common/WebSocket';
// require('dotenv').config();

const ENCRYPTION_ENABLED = process.env.REACT_APP_ENCRYPTION_ENABLED === 'true';

const handleEncryption: any = (data: any) => {
    if (ENCRYPTION_ENABLED) return encrypt(data);
    else return data;
};

const handleDecryption = (data: any) => {
    if (ENCRYPTION_ENABLED) return decrypt(data);
    else return data;
};

let isToastShown = false; // Global flag to ensure only one toast for 401 Unauthorized

class APIClient {
    private instance: any;

    constructor(baseURL: string) {
        this.instance = axios.create({
            baseURL,
            headers: { 'Content-Type': 'application/json' }
        });

        const authUser: any = AuthUser();
        const hasMultipleRoles = authUser?.userProfile?.roles?.length > 1;
        const userAuthority = {
            // role: authUser?.role,
            role: hasMultipleRoles ? store?.getState()?.SelectedRole?.role : authUser?.userProfile?.roles?.[0],
            privileges: authUser?.privileges
        };
        if (authUser) {
            this.instance.defaults.headers.common['Authorization'] = `Bearer ${authUser?.token}@$${encrypt(userAuthority)}`;
            // this.instance.defaults.headers.common['Authorization'] = `Bearer ${authUser?.token}@$`;

            this.instance.defaults.headers.common['X-ENCRYPTED'] = ENCRYPTION_ENABLED ? 'Y' : 'N';
        }

        this.instance.interceptors.response.use(
            (response: any) => {
                // Check custom flag from request config
                const shouldDecrypt = response.config?.decryptResponse !== false;

                // Conditionally decrypt response if ENCRYPTION_ENABLED is true
                const decryptedData = ENCRYPTION_ENABLED && shouldDecrypt && typeof response.data === 'string' ? JSON.parse(decrypt(response.data)) : response.data;
                return decryptedData ?? response;
            },
            (error: any) => {
                if (!navigator.onLine) {
                    window.location.href = '/#/auth-offline';
                    return; // prevent further error handling
                }
                let message: string;

                // Decrypt error response if encrypted
                const encryptedData = error?.response?.data;
                // Check custom flag from request config
                const shouldDecrypt = error.config?.decryptResponse !== false;

                const data =
                    ENCRYPTION_ENABLED && shouldDecrypt && typeof encryptedData === 'string'
                        ? JSON.parse(decrypt(encryptedData))
                        : encryptedData;

                switch (error?.response?.status) {
                    case 500:
                        window.location.href = '/#/auth-500';
                        break;

                    case 502:
                        message = 'Bad Gateway';
                        toast.error(message);
                        break;

                    case 503:
                        window.location.href = '/#/pages-maintenance';
                        break;

                    case 400:
                        message = data?.error || 'Bad Request';
                        toast.error(message);
                        break;

                    case 401:
                        if (!isToastShown) {
                            message = data?.message || 'Unauthorized';
                            toast.error(message);
                            isToastShown = true;
                        }
                        sessionStorage.clear();
                        localStorage.clear();
                        window.location.href = '/#/login';
                        break;

                    case 440:
                        // Session expired — handle if needed
                        break;
                    case 404:
                        // Not Found — handle if needed
                        break;
                    case 403:
                        return Promise.reject(error.response.status);
                    default:
                        message = data?.message || error.message || 'An unexpected error occurred';
                        toast.error(message);
                }
                return Promise.reject(error);
            }
        );
    }

    public get(url: string, params?: AxiosRequestConfig, token?: any) {
        // const tokenFromUrl = new URLSearchParams(window.location.search)?.get('resetToken');
        const tokenFromUrl = token?.token;

        const authUser: any = AuthUser();
        const hasMultipleRoles = authUser?.userProfile?.roles?.length > 1;
        const userAuthority = {
            role: hasMultipleRoles ? store?.getState()?.SelectedRole?.role : authUser?.userProfile?.roles?.[0],
            privileges: hasMultipleRoles ? store?.getState()?.SelectedRole?.privileges : authUser?.privileges
        };

        // Extract headers and preserve Authorization separately
        const headers = params?.headers || {};
        const { Authorization, ...otherHeaders } = headers;

        const encryptedHeaders = ENCRYPTION_ENABLED
            ? Object.fromEntries(Object.entries(otherHeaders).map(([key, value]) => [key, encrypt(value)]))
            : otherHeaders;

        const config: AxiosRequestConfig = {
            ...params,
            headers: {
                ...encryptedHeaders,
                ...(tokenFromUrl && {
                    Authorization: `Bearer ${tokenFromUrl}`
                })
            },
            params: params?.params // Preserve query params
        };

        // Encrypt path parameters if present and encryption is enabled
        let encryptedUrl = url;
        if (ENCRYPTION_ENABLED) {
            encryptedUrl = encryptDynamicPath(url);
        }

        // Send the GET request
        return this.instance.get(encryptedUrl, config).then((res: any) => {
            return res;
        });
    }

    // public create(url: string, data: any, isEncrypted = false, isMultipartFormData = false, isHeaders = false) {
    // 	if (isEncrypted) {
    // 		return encryptData(data)
    // 			.then(async (encryptedData: any) => {
    // 				return this.instance.post(url, { data: encryptedData });
    // 			})
    // 			.then(async (res: any) => {
    // 				if (res.status?.toLowerCase() === 'success') {
    // 					const decryptedResponse = await decryptData(res.data);
    // 					res.data = decryptedResponse;
    // 				}
    // 				return res;
    // 			});
    // 	} else {
    // 		const authUser: any = AuthUser();
    // 		const token: string | null = authUser ? authUser?.token : null;
    // 		return this?.instance?.post(url, isHeaders ? {} : data, {
    // 			headers: isHeaders
    // 				? { 'Content-Type': isMultipartFormData ? 'multipart/formdata' : 'application/json', ...data, Token: token }
    // 				: { 'Content-Type': isMultipartFormData ? 'multipart/formdata' : 'application/json' }
    // 		});
    // 	}
    // }

    // public async create(url: string, data: any, isMultipartFormData = false, isHeaders = false) {
    //     const headers: any = {
    //         'Content-Type': isMultipartFormData ? 'multipart/form-data' : 'application/json'
    //     };
    //     if (ENCRYPTION_ENABLED) {
    //         try {
    //             const encryptedData = encrypt(data);
    //             const res: any = await this.instance.post(url, { data: encryptedData }, { headers });
    //             const isSuccess = res.status?.toLowerCase?.() === 'success' || res.status === 200;
    //             if (isSuccess && res.data) {
    //                 const decryptedResponse = decrypt(res.data);
    //                 res.data = decryptedResponse;
    //             }
    //             return res;
    //         } catch (error) {
    //             return Promise.reject(error);
    //         }
    //     } else {
    //         const payload = isHeaders ? {} : data;
    //         const finalHeaders = isHeaders ? { ...headers, ...data } : headers;
    //         return this.instance.post(url, payload, { headers: finalHeaders });
    //     }
    // }

    public async create(url: string, data: any, isHeaders = false, avoidEncryption = false) {
        const shouldEncrypt = ENCRYPTION_ENABLED && !avoidEncryption;
        const headers: any = {
            'Content-Type': shouldEncrypt ? 'plain/text' : 'application/json'
        };

        // Encrypt path parameters if present and encryption is enabled
        let encryptedUrl = url;
        if (shouldEncrypt) {
            try {
                encryptedUrl = encryptDynamicPath(url);
                const encryptedData = encrypt(data);
                const res: any = await this.instance.post(encryptedUrl, encryptedData, { headers });
                return res;
            } catch (error) {
                return Promise.reject(error);
            }
        } else {
            const payload = isHeaders ? {} : data;
            const finalHeaders = isHeaders ? { ...headers, ...data } : headers;
            return this.instance.post(encryptedUrl, payload, { headers: finalHeaders });
        }
    }

    // public async put(url: string, data: any, isMultipartFormData = false, isHeaders = false) {
    //     const headers: any = {
    //         'Content-Type': isMultipartFormData ? 'multipart/form-data' : 'application/json'
    //     };
    //     if (ENCRYPTION_ENABLED) {
    //         try {
    //             const encryptedData = encrypt(data);
    //             const res: any = await this.instance.put(url, { data: encryptedData }, { headers });
    //             const isSuccess = typeof res.status === 'string' ? res.status.toLowerCase() === 'success' : res.status === 200;
    //             if (isSuccess && res.data) {
    //                 const decryptedResponse = decrypt(res.data);
    //                 res.data = decryptedResponse;
    //             }
    //             return res;
    //         } catch (error) {
    //             return Promise.reject(error);
    //         }
    //     } else {
    //         const payload = isHeaders ? {} : data;
    //         const finalHeaders = isHeaders ? { ...headers, ...data } : headers;
    //         return this.instance.put(url, payload, { headers: finalHeaders });
    //     }
    // }

    public async put(url: string, data: any, isMultipartFormData = false, isHeaders = false) {
        const headers: any = {
            'Content-Type': ENCRYPTION_ENABLED ? 'plain/text' : 'application/json'
        };

        // Encrypt path parameters if present and encryption is enabled
        let encryptedUrl = url;
        if (ENCRYPTION_ENABLED) {
            try {
                encryptedUrl = encryptDynamicPath(url);
                const encryptedData = encrypt(data);
                const res: any = await this.instance.put(encryptedUrl, encryptedData, { headers });
                return res;
            } catch (error) {
                return Promise.reject(error);
            }
        } else {
            const payload = isHeaders ? {} : data;
            const finalHeaders = isHeaders ? { ...headers, ...data } : headers;
            return this.instance.put(encryptedUrl, payload, { headers: finalHeaders });
        }
    }

    public uploadFile(url: string, data: any) {
        return this.instance.post(url, data, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            decryptResponse: false
        });
    }

    public deleteFile(url: string, params?: any) {
        return this.instance.get(url, {
            params,
            decryptResponse: false

        });
    }

    public uploadDocs(bucketName: string, url: string, data: any) {
        return this.instance
            .post(url, data, {
                headers: { 'Content-Type': 'multipart/formdata', Dirname: bucketName }
            })
            .then(async (res: any) => {
                if (res?.status?.toLowerCase() === 'success') {
                    res.data = await handleDecryption(res.data);
                }
                return res;
            });
    }

    // public delete(url: string, config?: AxiosRequestConfig) {
    //     return this.instance.delete(url, { ...config });
    // }

    public async delete(url: string, config?: AxiosRequestConfig) {
        if (ENCRYPTION_ENABLED) {
            try {
                const encryptedConfig = {
                    data: encrypt(config) // Encrypt data if it's part of the config
                };
                const res: any = await this.instance.delete(url, { ...encryptedConfig });
                return res;
            } catch (error) {
                return Promise.reject(error);
            }
        } else {
            return this.instance.delete(url, { ...config });
        }
    }

    public loginMethod(url: string, loginName: string, password: string) {
        const config: any = {
            headers: {
                loginName: ENCRYPTION_ENABLED ? encrypt(loginName) : loginName,
                password: ENCRYPTION_ENABLED ? encrypt(password) : password
            }
        };
        return this.instance.get(url, config).then((res: any) => {
            return res;
        });
    }
}

const getLoggedinUser = (): any => {
    const user = store.getState()?.Login?.user;
    return user ? user : null;
};

// Sets the default authorization
const setAuthorization = (tokenVal: string): void => {
    axios.defaults.headers.common['Authorization'] = 'Token ' + tokenVal;
};

export { APIClient, getLoggedinUser, setAuthorization };
